export interface StoredHandle {
  id: string
  handle: FileSystemDirectoryHandle
}

export class FileHandleStore {
  private db: IDBDatabase | null = null
  private readonly dbName: string
  private readonly storeName: string

  constructor(dbName = 'FileHandleDB', storeName = 'handles') {
    this.dbName = dbName
    this.storeName = storeName
  }

  async init(): Promise<void> {
    if (this.db) return
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }
      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id' })
        }
      }
    })
  }

  private async getAllHandles(): Promise<StoredHandle[]> {
    if (!this.db) throw new Error('Database not initialized')
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readonly')
      const store = transaction.objectStore(this.storeName)
      const request = store.getAll()
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result || [])
    })
  }

  private async getIdByHandle(newHandle: FileSystemDirectoryHandle): Promise<string | null> {
    const existingHandles = await this.getAllHandles()
    for (const stored of existingHandles) {
      try {
        if (await stored.handle.isSameEntry(newHandle)) {
          return stored.id
        }
      } catch (error) {
        console.error('Error checking handle:', error)
      }
    }
    return null
  }

  async saveHandle(dirHandle: FileSystemDirectoryHandle): Promise<string> {
    if (!this.db) throw new Error('Database not initialized')
    const existingId = await this.getIdByHandle(dirHandle)

    if (existingId) {
      return existingId
    }

    const id = Math.random().toString(36).substring(2)
    const storedHandle: StoredHandle = { id, handle: dirHandle }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.add(storedHandle)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(id)
    })
  }

  async removeHandle(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readwrite')
      const store = transaction.objectStore(this.storeName)
      const request = store.delete(id)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  async getHandleById(id: string): Promise<StoredHandle | null> {
    if (!this.db) throw new Error('Database not initialized')
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(this.storeName, 'readonly')
      const store = transaction.objectStore(this.storeName)
      const request = store.get(id)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result || null)
    })
  }

  public async isFolderExists(id: string): Promise<boolean> {
    try {
      const storedHandle = await this.getHandleById(id)
      if (!storedHandle) return false

      // Try to access the directory contents to verify it exists
      try {
        // Try to read at least one entry to confirm the directory is accessible
        const iterator = (storedHandle.handle as any).values()
        await iterator.next()

        // If we can get any entry (even if the directory is empty), the directory exists
        // In case of empty directory, done will be true but the folder still exists
        return true
      } catch (error) {
        console.warn(`Directory for handle ${id} appears to be inaccessible:`, error)
        return false
      }
    } catch (error) {
      console.error(`Error checking if folder exists for handle ${id}:`, error)
      return false
    }
  }
}

export async function listFilesAndFolders(directoryHandle: FileSystemDirectoryHandle) {
  try {
    const entries = []

    for await (const entry of (directoryHandle as any).values()) {
      const entryData = {
        name: entry.name,
        kind: entry.kind,
        handle: entry
      }

      entries.push(entryData)
    }

    return entries
  } catch (error) {
    console.error('Error listing files and folders:', error)
    throw error
  }
}
